import React, { useState, useEffect, useRef, useCallback } from 'react';
import { messagingAPI, usersAPI, vansAPI } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { useUnreadMessages } from '../../contexts/UnreadMessagesContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import {
  MessageSquare, Loader2, Send, User, Users, Truck, Plus, ArrowLeft, Clock,
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { toast } from 'sonner';

const formatLocalTime = (utcTimestamp, formatStr) => {
  if (!utcTimestamp) return '';
  try {
    let date;
    if (utcTimestamp.endsWith('Z')) {
      date = parseISO(utcTimestamp);
    } else if (utcTimestamp.includes('+') || utcTimestamp.includes('-', 10)) {
      date = parseISO(utcTimestamp);
    } else {
      date = parseISO(utcTimestamp + 'Z');
    }
    return format(date, formatStr);
  } catch {
    return format(new Date(utcTimestamp), formatStr);
  }
};

export default function TechMessages() {
  const { user } = useAuth();
  const { markConversationRead, unreadByConversation } = useUnreadMessages();
  const messagesEndRef = useRef(null);
  const pollIntervalRef = useRef(null);

  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [newConvDialogOpen, setNewConvDialogOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [vans, setVans] = useState([]);
  const [creatingConv, setCreatingConv] = useState(false);

  const fetchConversations = useCallback(async () => {
    try {
      const res = await messagingAPI.listConversations();
      setConversations(res.data);
    } catch (error) {
      console.error('Failed to load conversations:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchMessages = useCallback(async (conversationId) => {
    if (!conversationId) return;
    try {
      const res = await messagingAPI.getMessages(conversationId);
      setMessages(res.data);
    } catch (error) {
      console.error('Failed to load messages:', error);
    } finally {
      setMessagesLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConversations();
    const fetchMeta = async () => {
      // Fetch users and vans independently — techs may not have permission for users list
      try {
        const usersRes = await usersAPI.list();
        setUsers(usersRes.data.filter(u => u.id !== user.id && u.is_active));
      } catch { /* tech role may lack permission — silently skip */ }
      try {
        const vansRes = await vansAPI.list();
        setVans(vansRes.data.filter(v => v.is_active));
      } catch { /* silently skip */ }
    };
    fetchMeta();
    return () => {
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    };
  }, [fetchConversations, user?.id]);

  useEffect(() => {
    if (selectedConversation) {
      setMessagesLoading(true);
      fetchMessages(selectedConversation.id);
      markConversationRead(selectedConversation.id);
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = setInterval(() => {
        fetchMessages(selectedConversation.id);
        markConversationRead(selectedConversation.id);
      }, 10000);
    }
    return () => {
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    };
  }, [selectedConversation, fetchMessages, markConversationRead]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedConversation) return;
    setSendingMessage(true);
    try {
      const res = await messagingAPI.sendMessage(selectedConversation.id, newMessage.trim());
      setMessages(prev => [...prev, res.data]);
      setNewMessage('');
      fetchConversations();
      markConversationRead(selectedConversation.id);
    } catch {
      toast.error('Failed to send message');
    } finally {
      setSendingMessage(false);
    }
  };

  const handleCreateDM = async (targetUserId) => {
    setCreatingConv(true);
    try {
      const res = await messagingAPI.createDM(targetUserId);
      setConversations(prev => prev.find(c => c.id === res.data.id) ? prev : [res.data, ...prev]);
      setSelectedConversation(res.data);
      setNewConvDialogOpen(false);
    } catch {
      toast.error('Failed to create conversation');
    } finally {
      setCreatingConv(false);
    }
  };

  const handleOpenVehicleThread = async (vanId) => {
    setCreatingConv(true);
    try {
      const res = await messagingAPI.getVehicleThread(vanId);
      setConversations(prev => prev.find(c => c.id === res.data.id) ? prev : [res.data, ...prev]);
      setSelectedConversation(res.data);
      setNewConvDialogOpen(false);
    } catch {
      toast.error('Failed to open vehicle thread');
    } finally {
      setCreatingConv(false);
    }
  };

  const getTitle = (conv) => {
    if (conv.type === 'vehicle') return conv.vehicle_name || 'Vehicle Thread';
    const other = conv.members?.find(m => m.user_id !== user.id);
    return other?.user_name || 'Direct Message';
  };

  const getIcon = (conv) =>
    conv.type === 'vehicle'
      ? <Truck className="w-5 h-5 text-blue-600" />
      : <User className="w-5 h-5 text-blue-600" />;

  /* ── Conversation thread view ── */
  if (selectedConversation) {
    return (
      <div className="flex flex-col h-full" data-testid="tech-messages-thread">
        {/* Header */}
        <div className="flex items-center gap-3 px-4 py-3 bg-white border-b border-slate-200">
          <button
            onClick={() => setSelectedConversation(null)}
            className="p-1.5 -ml-1 rounded-lg active:bg-slate-100"
            data-testid="tech-messages-back-btn"
          >
            <ArrowLeft className="w-5 h-5 text-slate-700" />
          </button>
          <div className="w-9 h-9 rounded-full bg-blue-50 flex items-center justify-center shrink-0">
            {getIcon(selectedConversation)}
          </div>
          <div className="min-w-0 flex-1">
            <p className="font-bold text-sm text-slate-900 truncate">{getTitle(selectedConversation)}</p>
            {selectedConversation.type === 'vehicle' && (
              <p className="text-xs text-slate-400">{selectedConversation.members?.length || 0} members</p>
            )}
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3 bg-slate-50">
          {messagesLoading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
            </div>
          ) : messages.length === 0 ? (
            <p className="text-center text-sm text-slate-400 py-12">No messages yet. Say hello!</p>
          ) : (
            messages.map((msg) => {
              const mine = msg.user_id === user.id;
              return (
                <div key={msg.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
                  <div className="max-w-[80%]">
                    {!mine && (
                      <p className="text-[11px] font-semibold text-slate-500 mb-0.5 ml-1">{msg.user_name}</p>
                    )}
                    <div className={`rounded-2xl px-3.5 py-2 ${
                      msg.is_deleted
                        ? 'bg-slate-200 text-slate-400 italic'
                        : mine
                          ? 'bg-blue-600 text-white'
                          : 'bg-white text-slate-900 border border-slate-200'
                    }`}>
                      <p className="text-sm break-words">{msg.body}</p>
                    </div>
                    <p className="text-[10px] text-slate-400 mt-0.5 ml-1 flex items-center gap-1">
                      <Clock className="w-2.5 h-2.5" />
                      {formatLocalTime(msg.created_at, 'h:mm a')}
                    </p>
                  </div>
                </div>
              );
            })
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Compose */}
        <form onSubmit={handleSendMessage} className="flex items-center gap-2 px-3 py-2.5 bg-white border-t border-slate-200">
          <Input
            placeholder="Type a message..."
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            className="flex-1 h-10 text-sm bg-slate-50 border-slate-200"
            data-testid="tech-messages-input"
          />
          <Button
            type="submit"
            size="icon"
            disabled={sendingMessage || !newMessage.trim()}
            className="h-10 w-10 shrink-0 bg-blue-600 hover:bg-blue-700"
            data-testid="tech-messages-send-btn"
          >
            {sendingMessage ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </Button>
        </form>
      </div>
    );
  }

  /* ── Conversations list view ── */
  return (
    <div className="flex flex-col h-full" data-testid="tech-messages">
      {/* Header */}
      <div className="flex items-center justify-between px-4 pt-6 pb-3">
        <h1 className="text-xl font-bold text-slate-900">Messages</h1>
        <button
          onClick={() => setNewConvDialogOpen(true)}
          className="w-9 h-9 flex items-center justify-center rounded-full bg-blue-600 text-white active:bg-blue-700"
          data-testid="tech-messages-new-btn"
        >
          <Plus className="w-5 h-5" strokeWidth={2.5} />
        </button>
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
          </div>
        ) : conversations.length === 0 ? (
          <div className="text-center py-16 px-6">
            <MessageSquare className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <p className="text-sm font-medium text-slate-500">No conversations yet</p>
            <p className="text-xs text-slate-400 mt-1">Tap + to start a new one</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {conversations.map((conv) => {
              const unread = unreadByConversation[conv.id] || 0;
              return (
                <button
                  key={conv.id}
                  className="w-full flex items-center gap-3 px-4 py-3.5 text-left active:bg-slate-50 transition-colors"
                  onClick={() => setSelectedConversation(conv)}
                  data-testid={`tech-conv-${conv.id}`}
                >
                  <div className="relative w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center shrink-0">
                    {getIcon(conv)}
                    {unread > 0 && (
                      <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] bg-red-500 text-[10px] font-bold text-white rounded-full flex items-center justify-center px-1">
                        {unread > 9 ? '9+' : unread}
                      </span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm truncate ${unread ? 'font-bold text-slate-900' : 'font-semibold text-slate-800'}`}>
                      {getTitle(conv)}
                    </p>
                    {conv.last_message && (
                      <p className={`text-xs truncate mt-0.5 ${unread ? 'font-medium text-slate-700' : 'text-slate-400'}`}>
                        {conv.last_message}
                      </p>
                    )}
                  </div>
                  {conv.last_message_at && (
                    <span className="text-[10px] text-slate-400 shrink-0">
                      {formatLocalTime(conv.last_message_at, 'h:mm a')}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* New Conversation Dialog */}
      <Dialog open={newConvDialogOpen} onOpenChange={setNewConvDialogOpen}>
        <DialogContent className="max-w-[calc(100vw-2rem)] rounded-xl">
          <DialogHeader>
            <DialogTitle className="text-base font-bold">New Conversation</DialogTitle>
            <DialogDescription className="text-xs">Start a direct message or open a vehicle thread</DialogDescription>
          </DialogHeader>
          <div className="space-y-5 mt-2">
            <div>
              <h3 className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-2 flex items-center gap-1.5">
                <User className="w-3.5 h-3.5" /> Direct Message
              </h3>
              <div className="space-y-1.5 max-h-36 overflow-y-auto">
                {users.length === 0 ? (
                  <p className="text-xs text-slate-400 text-center py-3">No other users</p>
                ) : users.map((u) => (
                  <button
                    key={u.id}
                    className="w-full flex items-center gap-3 p-2.5 rounded-lg border border-slate-200 active:border-blue-400 text-left"
                    onClick={() => !creatingConv && handleCreateDM(u.id)}
                    data-testid={`tech-dm-user-${u.id}`}
                  >
                    <div className="w-8 h-8 bg-blue-50 rounded-full flex items-center justify-center">
                      <User className="w-4 h-4 text-blue-600" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-slate-900 truncate">{u.name}</p>
                      <p className="text-[11px] text-slate-400 capitalize">{u.role}</p>
                    </div>
                    {creatingConv && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <h3 className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-2 flex items-center gap-1.5">
                <Truck className="w-3.5 h-3.5" /> Vehicle Threads
              </h3>
              <div className="space-y-1.5 max-h-36 overflow-y-auto">
                {vans.length === 0 ? (
                  <p className="text-xs text-slate-400 text-center py-3">No vehicles</p>
                ) : vans.map((v) => (
                  <button
                    key={v.id}
                    className="w-full flex items-center gap-3 p-2.5 rounded-lg border border-slate-200 active:border-blue-400 text-left"
                    onClick={() => !creatingConv && handleOpenVehicleThread(v.id)}
                    data-testid={`tech-vehicle-thread-${v.id}`}
                  >
                    <div className="w-8 h-8 bg-blue-50 rounded-full flex items-center justify-center">
                      <Truck className="w-4 h-4 text-blue-600" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-slate-900 truncate">{v.name}</p>
                      <p className="text-[11px] text-slate-400">{v.van_number || 'No number'}</p>
                    </div>
                    {creatingConv && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
